import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navLinks } from '../data/siteData';
import { logos } from '../assets';
import { getPublicNavigation } from '../api/api';
import './Navbar.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('tta-theme') === 'dark');
  const [cmsNav, setCmsNav] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('tta-theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    getPublicNavigation()
      .then(data => {
        // L'API retourne {"data": [...]}
        const items = data?.data ?? (Array.isArray(data) ? data : null);
        setCmsNav(Array.isArray(items) ? items : null);
      })
      .catch(() => setCmsNav(null));
  }, []);

  // Ferme le dropdown au clic extérieur
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Items depuis CMS ou fallback siteData
  const navItems = cmsNav
    ? cmsNav.map(item => ({
        label: item.label,
        href: item.href,
        children: item.children || [],
      }))
    : navLinks.map(link => ({ ...link, children: [] }));

  const toggleMobileExpand = (href) => {
    setMobileExpanded(prev => ({ ...prev, [href]: !prev[href] }));
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${menuOpen ? 'navbar--open' : ''}`}>
      <div className="navbar__inner">
        <Link to="/" className="navbar__logo">
          <img src={dark ? logos.dark : logos.light} alt="Tech Talent Accelerator" />
        </Link>

        <ul className="navbar__links" ref={dropdownRef}>
          {navItems.map(item => (
            <li key={item.href} className={item.children.length > 0 ? 'navbar__has-dropdown' : ''}>
              {item.children.length > 0 ? (
                <div className="navbar__dropdown-wrap">
                  <button
                    className={`navbar__link navbar__link--btn ${
                      location.pathname.startsWith(item.href) ? 'navbar__link--active' : ''
                    }`}
                    onClick={() => setOpenDropdown(openDropdown === item.href ? null : item.href)}
                  >
                    {item.label}
                    <svg
                      className={`navbar__chevron ${openDropdown === item.href ? 'navbar__chevron--open' : ''}`}
                      width="11" height="11" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2.5"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {openDropdown === item.href && (
                    <div className="navbar__dropdown">
                      {item.children.map(child => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="navbar__dropdown-item"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`navbar__link ${location.pathname === item.href ? 'navbar__link--active' : ''}`}
                >
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="navbar__right">
          <button
            className="navbar__theme-toggle"
            onClick={() => setDark(d => !d)}
            aria-label="Changer le thème"
            title={dark ? 'Passer en mode clair' : 'Passer en mode sombre'}
          >
            {dark ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          <Link to="/candidater" className="navbar__cta">Candidater →</Link>
        </div>

        <button className="navbar__burger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </div>

      {/* Menu mobile */}
      <div className={`navbar__mobile ${menuOpen ? 'navbar__mobile--open' : ''}`}>
        {navItems.map(item => (
          <div key={item.href}>
            {item.children.length > 0 ? (
              <>
                <button
                  className={`navbar__mobile-link navbar__mobile-link--expand ${
                    location.pathname.startsWith(item.href) ? 'active' : ''
                  }`}
                  onClick={() => toggleMobileExpand(item.href)}
                >
                  {item.label}
                  <svg
                    className={`navbar__chevron ${mobileExpanded[item.href] ? 'navbar__chevron--open' : ''}`}
                    width="11" height="11" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {mobileExpanded[item.href] && (
                  <div className="navbar__mobile-submenu">
                    {item.children.map(child => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="navbar__mobile-link navbar__mobile-link--child"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.href}
                className={`navbar__mobile-link ${location.pathname === item.href ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            )}
          </div>
        ))}
        <Link to="/candidater" className="btn-primary" style={{ marginTop: '12px', justifyContent: 'center' }}>
          Candidater →
        </Link>
      </div>
    </nav>
  );
}
